import { useMemo, useState } from 'react'
import { Plane, X, ChevronDown, ChevronUp } from 'lucide-react'
import type { MonitorLiveAircraft } from '@/lib/api'

const MILITARY_PATTERNS = /^(RCH|REACH|EVAC|VIPER|EAGLE|DUKE|NAVY|HUSKY|BLUE|COBRA|SNAKE|TIGER|BONE|SPAR|ROOK|MASH|DUSTOFF|MOOSE|HERKY|CALLSIGN|PAE|OMF|PAT|HAWK|RAPTOR|STRIKE|WOLF|BEAR|LION|HOG)/i

function isMilitary(a: MonitorLiveAircraft): boolean {
  const cs = (a.callsign || '').toUpperCase()
  const country = (a.origin_country || '').toUpperCase()
  const militaryCountries = ['RU', 'CN', 'KP', 'IR', 'SY', 'US', 'GB', 'FR', 'DE', 'IL', 'IN', 'PK', 'SA']
  return MILITARY_PATTERNS.test(cs) || militaryCountries.includes(country)
}

type SortKey = 'altitude' | 'speed' | 'callsign' | 'country'

interface Props {
  aircraft: MonitorLiveAircraft[]
  selected: MonitorLiveAircraft | null
  onSelect: (a: MonitorLiveAircraft | null) => void
  onClose: () => void
}

export default function AircraftDetailPanel({ aircraft, selected, onSelect, onClose }: Props) {
  const [sortBy, setSortBy] = useState<SortKey>('altitude')
  const [militaryOnly, setMilitaryOnly] = useState(false)
  const [collapsed, setCollapsed] = useState(false)

  const sorted = useMemo(() => {
    let list = [...aircraft]
    if (militaryOnly) list = list.filter(isMilitary)
    switch (sortBy) {
      case 'altitude':
        list.sort((a, b) => (b.baro_altitude ?? -1) - (a.baro_altitude ?? -1))
        break
      case 'speed':
        list.sort((a, b) => (b.velocity ?? -1) - (a.velocity ?? -1))
        break
      case 'callsign':
        list.sort((a, b) => (a.callsign || a.icao24).localeCompare(b.callsign || b.icao24))
        break
      case 'country':
        list.sort((a, b) => (a.origin_country || '').localeCompare(b.origin_country || ''))
        break
    }
    return list
  }, [aircraft, sortBy, militaryOnly])

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded-lg shadow-xl overflow-hidden max-w-sm shadow-[0_0_20px_rgba(34,197,94,0.1)]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-green-900/40">
        <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-green-400 drop-shadow-[0_0_4px_rgba(74,222,128,0.3)]">
          <Plane className="w-4 h-4" /> Aircraft ({sorted.length})
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1 rounded text-gray-500 hover:text-green-300 hover:bg-gray-800/50"
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <button onClick={onClose} className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="p-2 space-y-2 border-b border-gray-800/50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] text-gray-600 uppercase font-mono">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortKey)}
                className="bg-gray-800/70 border border-gray-700/40 rounded px-2 py-0.5 text-[9px] text-gray-400 font-mono focus:outline-none focus:border-green-500/50"
              >
                <option value="altitude">Altitude</option>
                <option value="speed">Speed</option>
                <option value="callsign">Callsign</option>
                <option value="country">Country</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={militaryOnly}
                onChange={(e) => setMilitaryOnly(e.target.checked)}
                className="rounded border-gray-600 bg-gray-800"
              />
              <span className="text-[9px] font-mono text-red-400">Military only</span>
            </label>
          </div>

          <div className="max-h-[40vh] overflow-y-auto">
            {sorted.length === 0 ? (
              <div className="px-4 py-6 text-center text-[10px] text-gray-600 font-mono">
                {militaryOnly ? 'No military aircraft' : 'No aircraft in view'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-800/30">
                {sorted.slice(0, 80).map((a) => {
                  const military = isMilitary(a)
                  const isSel = selected?.icao24 === a.icao24
                  return (
                    <li key={a.icao24}>
                      <button
                        onClick={() => onSelect(isSel ? null : a)}
                        className={`w-full text-left px-2 py-1.5 hover:bg-green-500/10 transition-colors ${
                          isSel ? 'bg-green-500/15 border-l-2 border-green-400' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-mono text-gray-300 truncate">
                            {a.callsign || a.icao24}
                            {military && <span className="text-red-400 ml-1">[MIL]</span>}
                          </span>
                          <span className="text-[8px] text-gray-600 flex-shrink-0">
                            {(a.baro_altitude ?? 0) / 1000}km · {(a.velocity ?? 0) * 1.943}kt
                          </span>
                        </div>
                        {a.origin_country && (
                          <div className="text-[8px] text-gray-600 mt-0.5">{a.origin_country}</div>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  )
}
